import { useEffect, useState, type ReactNode } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useRealtimeOperatorEvents } from "./lib/realtime";
import { useAuthStore } from "./lib/auth";
import { useTenantMe } from "./lib/tenant-me";
import { useUserMe } from "./lib/user-me";
import { AppShell } from "./components/v2/AppShell";
import { CommandPalette } from "./components/v2/CommandPalette";
import { CommandPaletteContext } from "./lib/command-palette";
import { SignedOut } from "./pages/SignedOut";
import { VerifyEmail } from "./pages/VerifyEmail";
import { OnboardingShell } from "./pages/onboarding/OnboardingShell";
import { PersonalStep } from "./pages/onboarding/PersonalStep";
import { WorkspaceStep } from "./pages/onboarding/WorkspaceStep";
import { InviteStep } from "./pages/onboarding/InviteStep";
import { ServicesStep } from "./pages/onboarding/ServicesStep";
import { PlanStep } from "./pages/onboarding/PlanStep";
import { WelcomeStep } from "./pages/onboarding/WelcomeStep";
import { DoneStep } from "./pages/onboarding/DoneStep";
import { WelcomePersonalStep } from "./pages/onboarding/WelcomePersonalStep";
import { Home } from "./pages/Home";
import { Services } from "./pages/Services";
import { Bookings } from "./pages/Bookings";
import { BookingDetail } from "./pages/BookingDetail";
import { Clients } from "./pages/Clients";
import { Inventory } from "./pages/Inventory";
import { InventoryCategory } from "./pages/InventoryCategory";
import { Transactions } from "./pages/Transactions";
import { Insights } from "./pages/Insights";
import { Calendar } from "./pages/Calendar";
// Phase F: split settings into /account/* (USER) and /workspace/* (WORKSPACE).
import { AccountLayout } from "./pages/account/AccountLayout";
import { AccountProfile } from "./pages/account/Profile";
import { AccountEmail } from "./pages/account/Email";
import { AccountSecurity } from "./pages/account/Security";
import { AccountSessions } from "./pages/account/Sessions";
import { AccountPreferences } from "./pages/account/Preferences";
import { AccountNotifications } from "./pages/account/Notifications";
import { AccountDangerZone } from "./pages/account/DangerZone";
import { WorkspaceLayout } from "./pages/workspace/WorkspaceLayout";
import { WorkspaceGeneral } from "./pages/workspace/General";
import { WorkspaceRegional } from "./pages/workspace/Regional";
import { WorkspaceNotifications } from "./pages/workspace/Notifications";
import { WorkspaceTeam } from "./pages/workspace/Team";
import { WorkspaceBilling } from "./pages/workspace/Billing";
import { WorkspacePaymentMethods } from "./pages/workspace/PaymentMethods";
import { WorkspaceCustomFields } from "./pages/workspace/CustomFields";
import { ServiceDetail } from "./pages/ServiceDetail";
import { ServiceOverview } from "./pages/service/Overview";
import { ServiceBookings } from "./pages/service/Bookings";
import { ServiceClients } from "./pages/service/Clients";
import { ServiceInventory } from "./pages/service/Inventory";
import { ServiceCustomFields } from "./pages/service/CustomFields";
import { ServiceSettings } from "./pages/service/Settings";
import { NotFoundPage } from "./pages/NotFoundPage";
import { ErrorBoundary } from "./components/ErrorBoundary";

export default function App() {
  // Single STOMP session for the whole app — invalidates react-query caches
  // on relevant outbox events (payment, booking, inventory).
  useRealtimeOperatorEvents();

  const accessToken = useAuthStore((s) => s.accessToken);
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Global ⌘K listener. Toggles the command palette regardless of which input
  // has focus — matches the convention used by every modern web app + IDE.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Authenticated screens render inside the AppShell (sidebar + topbar).
  // The SignedOut card is full-page so an anonymous visitor doesn't see
  // the chrome. Routes above this gate (e.g. /verify-email) render
  // regardless — those endpoints validate their own credential (a magic
  // link in the URL) and shouldn't bounce a visitor to sign-in.
  return (
    <ErrorBoundary>
    <CommandPaletteContext.Provider value={{ open: () => setPaletteOpen(true) }}>
      <Routes>
        {/* Public — works whether or not the visitor has a session. */}
        <Route path="/verify-email" element={<VerifyEmail />} />

        {/* Everything below requires an access token. */}
        <Route
          path="*"
          element={accessToken ? <AuthedRouter /> : <SignedOut />}
        />
      </Routes>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </CommandPaletteContext.Provider>
    </ErrorBoundary>
  );
}

/**
 * Everything that requires a session lives here. Pulled out of the
 * top-level App component so a public route (`/verify-email`) can sit
 * alongside the gated `<Routes>` block without duplicating the
 * SignedOut / accessToken short-circuit.
 */
function AuthedRouter() {
  return (
    <Routes>
        {/* Welcome-Personal (Tier-2 mini onboarding for invited users)
            renders outside the wizard shell — it's a single-step
            screen for already-invited users completing their profile. */}
        <Route path="/welcome-personal" element={<WelcomePersonalStep />} />

        {/* Onboarding wizard — Notion-style sidebar shell. Welcome
            and Done are folded in as full members so the entire flow
            feels like one continuous experience (Phase K redesign). */}
        <Route path="/onboarding" element={<OnboardingShell />}>
          <Route index element={<Navigate to="welcome" replace />} />
          <Route path="welcome" element={<WelcomeStep />} />
          <Route path="personal" element={<PersonalStep />} />
          <Route path="workspace" element={<WorkspaceStep />} />
          <Route path="invite" element={<InviteStep />} />
          <Route path="services" element={<ServicesStep />} />
          <Route path="plan" element={<PlanStep />} />
          <Route path="done" element={<DoneStep />} />
        </Route>

        {/* Everything else lives inside AppShell + onboarding gate. */}
        <Route
          path="*"
          element={
            <RequireOnboardingDone>
              <AppShell>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/dashboard" element={<Home />} />
                  <Route path="/bookings" element={<Bookings />} />
                  <Route path="/bookings/:id" element={<BookingDetail />} />
                  <Route path="/calendar" element={<Calendar />} />
                  <Route path="/clients" element={<Clients />} />
                  <Route path="/services" element={<Services />} />
                  <Route path="/services/:id" element={<ServiceDetail />}>
                    <Route index element={<Navigate to="overview" replace />} />
                    <Route path="overview" element={<ServiceOverview />} />
                    <Route path="bookings" element={<ServiceBookings />} />
                    <Route path="clients" element={<ServiceClients />} />
                    <Route path="inventory" element={<ServiceInventory />} />
                    <Route path="custom-fields" element={<ServiceCustomFields />} />
                    <Route path="settings" element={<ServiceSettings />} />
                  </Route>
                  <Route path="/inventory" element={<Inventory />} />
                  <Route path="/inventory/:category" element={<InventoryCategory />} />
                  <Route path="/transactions" element={<Transactions />} />
                  <Route path="/insights" element={<Insights />} />

                  {/* USER namespace */}
                  <Route path="/account" element={<AccountLayout />}>
                    <Route index element={<Navigate to="profile" replace />} />
                    <Route path="profile" element={<AccountProfile />} />
                    <Route path="email" element={<AccountEmail />} />
                    <Route path="security" element={<AccountSecurity />} />
                    <Route path="sessions" element={<AccountSessions />} />
                    <Route path="preferences" element={<AccountPreferences />} />
                    <Route path="notifications" element={<AccountNotifications />} />
                    <Route path="danger-zone" element={<AccountDangerZone />} />
                    <Route path="*" element={<Navigate to="/account/profile" replace />} />
                  </Route>

                  {/* WORKSPACE (tenant) namespace */}
                  <Route path="/workspace" element={<WorkspaceLayout />}>
                    <Route index element={<Navigate to="general" replace />} />
                    <Route path="general" element={<WorkspaceGeneral />} />
                    <Route path="regional" element={<WorkspaceRegional />} />
                    <Route path="notifications" element={<WorkspaceNotifications />} />
                    <Route path="team" element={<WorkspaceTeam />} />
                    <Route path="billing" element={<WorkspaceBilling />} />
                    <Route path="payment-methods" element={<WorkspacePaymentMethods />} />
                    <Route path="custom-fields" element={<WorkspaceCustomFields />} />
                    <Route path="*" element={<Navigate to="/workspace/general" replace />} />
                  </Route>

                  {/* Legacy /settings/* redirects — keeps bookmarks working */}
                  <Route path="/settings" element={<Navigate to="/account/profile" replace />} />
                  <Route path="/settings/account" element={<Navigate to="/account/profile" replace />} />
                  <Route path="/settings/notifications" element={<Navigate to="/account/notifications" replace />} />
                  <Route path="/settings/workspace" element={<Navigate to="/workspace/general" replace />} />
                  <Route path="/settings/team" element={<Navigate to="/workspace/team" replace />} />
                  <Route path="/settings/billing" element={<Navigate to="/workspace/billing" replace />} />
                  <Route path="/settings/payment-methods" element={<Navigate to="/workspace/payment-methods" replace />} />
                  <Route path="/settings/custom-fields" element={<Navigate to="/workspace/custom-fields" replace />} />

                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </AppShell>
            </RequireOnboardingDone>
          }
        />
    </Routes>
  );
}

/**
 * Redirects fresh signups (tenant.onboardingCompletedAt == null) to the
 * onboarding wizard. Once the wizard finishes (or for tenants who already
 * completed it), renders the children unchanged.
 */
function RequireOnboardingDone({ children }: { children: ReactNode }) {
  const me = useTenantMe();
  const userMe = useUserMe();
  const loc = useLocation();

  // Spinner while we don't yet know — the user might be a fresh signup.
  // Splash is visually quiet so it doesn't flash before either branch.
  if (me.isLoading || userMe.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }

  // If /tenants/me errored (auth blip etc.) just render through — the
  // child query will surface its own error and the auth-fetch wrapper
  // already handles 401 → re-auth bounce.
  if (me.isError || !me.data) return <>{children}</>;

  const tenantOnboarded = me.data.tenant.onboardingCompletedAt != null;
  const userHasName = !!userMe.data?.firstName?.trim();

  // Tier 1 — first user / OWNER on a fresh tenant: full wizard.
  if (!tenantOnboarded) {
    if (!loc.pathname.startsWith("/onboarding")) {
      // Brand-new signup (no first name) → Welcome splash.
      // Existing operator (has first name) creating a new workspace
      // → skip Personal entirely, land on Workspace. Personal would
      // re-prompt for identity that's already on file and risk
      // unintentional drift between this user row and the original.
      return (
        <Navigate
          to={userHasName ? "/onboarding/workspace" : "/onboarding/welcome"}
          replace
        />
      );
    }
  } else if (!userHasName && loc.pathname !== "/welcome-personal") {
    // Tier 2 — invited user joining a fully-set-up workspace, but
    // their own profile has no first name yet. Mini-onboarding —
    // Personal info only, no Workspace / Invite / Services / Plan.
    return <Navigate to="/welcome-personal" replace />;
  }

  return <>{children}</>;
}
